// Extract text from a (possibly large) PDF for the lecture pipeline's source_text.
//
// >10 MB PDFs exceed the /files/extract-text cap, so we split the PDF into
// sub-PDFs under the cap (pdf-lib, lazy-loaded), extract each like a normal
// upload, and concatenate. The FULL PDF still renders locally for marking.
//
// Shared by lecture-start (Create flow) and the standalone Study "generate a
// set from these marks" action so both produce identical source_text.
import { extractTextFromFile } from "@/api/files";

export async function extractPdfText(
  f: File,
  onProgress?: (msg: string, parts?: number) => void,
): Promise<string> {
  const SINGLE_MAX = 9.5 * 1024 * 1024;
  if (f.size <= SINGLE_MAX) {
    onProgress?.("Extracting text…");
    const r = await extractTextFromFile(f);
    return r.text;
  }
  const { PDFDocument } = await import("pdf-lib");
  const bytes = new Uint8Array(await f.arrayBuffer());
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const total = doc.getPageCount();
  const CHUNK_TARGET = 8 * 1024 * 1024;
  const pagesPerChunk = Math.max(1, Math.floor((total * CHUNK_TARGET) / f.size));
  const ranges: [number, number][] = [];
  for (let s = 0; s < total; s += pagesPerChunk) ranges.push([s, Math.min(s + pagesPerChunk, total)]);
  const texts: string[] = [];
  for (let i = 0; i < ranges.length; i++) {
    const [s, e] = ranges[i];
    onProgress?.(`Splitting & extracting — part ${i + 1} of ${ranges.length}…`, ranges.length);
    const sub = await PDFDocument.create();
    const copied = await sub.copyPages(doc, Array.from({ length: e - s }, (_, k) => s + k));
    copied.forEach((p) => sub.addPage(p));
    const out = await sub.save();
    // pdf-lib returns Uint8Array<ArrayBufferLike>; a valid BlobPart at runtime,
    // but newer DOM lib types reject it — cast to satisfy tsc.
    const partFile = new File(
      [out as unknown as BlobPart],
      `part-${i + 1}.pdf`,
      { type: "application/pdf" },
    );
    try {
      const r = await extractTextFromFile(partFile);
      texts.push(`## Part ${i + 1} (pages ${s + 1}–${e})\n\n${r.text}`.trim());
    } catch {
      texts.push(`## Part ${i + 1} (pages ${s + 1}–${e})`);
    }
  }
  return texts.join("\n\n");
}
